import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardCheck,
  faFilePdf,
  faCheck,
  faClock,
  faPenNib,
  faStar,
  faSquareCheck,
  faCircleDot,
  faListCheck,
  faTextWidth,
  faAlignLeft,
  faArrowLeft,
  faArrowRight,
  faSave,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import api from '../api/axios';
import { toast } from 'react-toastify';

const questionTypeIcons = {
  text: faTextWidth,
  textarea: faAlignLeft,
  checkbox: faSquareCheck,
  multi_checkbox: faListCheck,
  radio: faCircleDot,
  rating: faStar,
  signature: faPenNib
};

const ClientFeedback = ({ user }) => {
  const [cycles, setCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const [representativeName, setRepresentativeName] = useState('');
  const [designation, setDesignation] = useState('');
  const [activeSection, setActiveSection] = useState(0);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const fetchMyCycles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/feedback/my-cycles');
      setCycles(res.data);
    } catch (err) {
      toast.error('Failed to load your feedback forms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyCycles();
  }, [fetchMyCycles]);

  const loadCycle = async (cycleId) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/feedback/cycles/${cycleId}`);
      setSelectedCycle(res.data);
      
      // Initialize responses from existing data
      const existingResponses = {};
      res.data.sections?.forEach(section => {
        section.questions?.forEach(q => {
          if (q.response) {
            existingResponses[q.id] = q.response;
          }
        });
      });
      setResponses(existingResponses);
      
      // Pre-fill signature data if exists
      if (res.data.signature_file) {
        setSignatureData(res.data.signature_file);
        setRepresentativeName(res.data.representative_name || '');
        setDesignation(res.data.designation || '');
      }
      
      setActiveSection(0);
    } catch (err) {
      toast.error('Failed to load feedback form');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const getQuestionResponse = (question) => {
    return responses[question.id] || {};
  };

  const submitFeedback = async () => {
    // Validate required fields
    let missingRequired = [];
    selectedCycle.sections?.forEach(section => {
      section.questions?.forEach(q => {
        if (q.required) {
          const response = responses[q.id];
          if (!response || 
              (q.question_type === 'text' && !response.text) ||
              (q.question_type === 'textarea' && !response.text) ||
              (q.question_type === 'rating' && !response.rating) ||
              (q.question_type === 'radio' && !response.selected) ||
              (q.question_type === 'multi_checkbox' && (!response.selected || response.selected.length === 0))) {
            missingRequired.push(q.question_text);
          }
        }
      });
    });

    if (missingRequired.length > 0) {
      toast.error(`Please answer all required questions: ${missingRequired.slice(0, 3).join(', ')}${missingRequired.length > 3 ? '...' : ''}`);
      return;
    }

    try {
      setSubmitting(true);
      
      const formattedResponses = Object.entries(responses).map(([questionId, response]) => ({
        question_id: parseInt(questionId),
        response
      }));

      await api.post(`/api/feedback/cycles/${selectedCycle.id}/submit`, {
        responses: formattedResponses
      });

      toast.success('Feedback submitted successfully!');
      fetchMyCycles();
      setSelectedCycle(prev => ({ ...prev, status: 'SUBMITTED' }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  // Signature pad functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (showSignaturePad) {
      setTimeout(initCanvas, 100);
    }
  }, [showSignaturePad]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const openOrDownloadPdf = async (cycleId, mode = 'download') => {
    try {
      const res = await api.get(`/api/feedback/cycles/${cycleId}/pdf/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (mode === 'view') {
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }

      const a = document.createElement('a');
      a.href = url;
      a.download = `feedback_${cycleId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download PDF');
    }
  };

  const saveSignature = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Check if canvas is empty
    const ctx = canvas.getContext('2d');
    const pixelBuffer = new Uint32Array(ctx.getImageData(0, 0, canvas.width, canvas.height).data.buffer);
    const isEmpty = pixelBuffer.every(color => color === 0);
    
    if (isEmpty) {
      toast.error('Please sign before saving');
      return;
    }

    if (!representativeName.trim()) {
      toast.error('Please enter representative name');
      return;
    }

    try {
      const signatureBase64 = canvas.toDataURL('image/png');
      
      await api.post(`/api/feedback/cycles/${selectedCycle.id}/sign`, {
        representative_name: representativeName,
        designation,
        signature_data: signatureBase64
      });

      setSignatureData(signatureBase64);
      setShowSignaturePad(false);
      toast.success('Signature saved successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save signature');
    }
  };

  const generatePdf = async () => {
    try {
      const res = await api.get(`/api/feedback/cycles/${selectedCycle.id}/pdf`);
      toast.success('PDF generated');
      await openOrDownloadPdf(selectedCycle.id, 'download');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate PDF');
    }
  };

  const renderQuestionInput = (question) => {
    const response = getQuestionResponse(question);
    
    switch (question.question_type) {
      case 'text':
        return (
          <input
            type="text"
            className="glass-input w-full"
            value={response.text || ''}
            onChange={e => handleResponseChange(question.id, { text: e.target.value })}
            placeholder="Enter your answer"
            disabled={selectedCycle.status !== 'PENDING'}
          />
        );
        
      case 'textarea':
        return (
          <textarea
            className="glass-textarea w-full"
            value={response.text || ''}
            onChange={e => handleResponseChange(question.id, { text: e.target.value })}
            placeholder="Enter your detailed response"
            rows={4}
            disabled={selectedCycle.status !== 'PENDING'}
          />
        );
        
      case 'rating':
        return (
          <div className="rating-input-modern">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                type="button"
                className={`rating-star-modern ${(response.rating || 0) >= star ? 'active' : ''}`}
                onClick={() => selectedCycle.status === 'PENDING' && handleResponseChange(question.id, { rating: star })}
                disabled={selectedCycle.status !== 'PENDING'}
              >
                <FontAwesomeIcon icon={faStar} />
              </button>
            ))}
            <span className="rating-label-modern">{response.rating ? `${response.rating}/5` : 'Click to rate'}</span>
          </div>
        );
        
      case 'checkbox':
        return (
          <label className="modern-checkbox-label">
            <input
              type="checkbox"
              className="modern-checkbox"
              checked={response.checked || false}
              onChange={e => handleResponseChange(question.id, { checked: e.target.checked })}
              disabled={selectedCycle.status !== 'PENDING'}
            />
            <span className="checkbox-text">Yes, I agree / Confirm</span>
          </label>
        );
        
      case 'radio':
        return (
          <div className="modern-radio-group">
            {question.options?.map((option, idx) => (
              <label key={idx} className="modern-radio-label">
                <input
                  type="radio"
                  className="modern-radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={response.selected === option}
                  onChange={e => handleResponseChange(question.id, { selected: e.target.value })}
                  disabled={selectedCycle.status !== 'PENDING'}
                />
                <span className="radio-text">{option}</span>
              </label>
            ))}
          </div>
        );
        
      case 'multi_checkbox':
        return (
          <div className="modern-checkbox-group">
            {question.options?.map((option, idx) => (
              <label key={idx} className="modern-checkbox-label">
                <input
                  type="checkbox"
                  className="modern-checkbox"
                  value={option}
                  checked={(response.selected || []).includes(option)}
                  onChange={e => {
                    const current = response.selected || [];
                    const updated = e.target.checked 
                      ? [...current, option]
                      : current.filter(o => o !== option);
                    handleResponseChange(question.id, { selected: updated });
                  }}
                  disabled={selectedCycle.status !== 'PENDING'}
                />
                <span className="checkbox-text">{option}</span>
              </label>
            ))}
          </div>
        );
        
      default:
        return null;
    }
  };

  if (selectedCycle) {
    const isSubmitted = selectedCycle.status !== 'PENDING';
    const allSections = selectedCycle.sections || [];
    const currentSection = allSections[activeSection] || {};
    
    return (
      <div className="client-feedback-form">
        <div className="form-header-modern">
          <button className="glass-button back-btn" onClick={() => setSelectedCycle(null)}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to My Feedback
          </button>
          <div className="header-content">
            <h1>{selectedCycle.template_name}</h1>
            <div className="form-meta-modern">
              <span className="project-badge">{selectedCycle.project_title}</span>
              <span className={`status-badge-modern ${selectedCycle.status.toLowerCase()}`}>
                {selectedCycle.status}
              </span>
            </div>
          </div>
        </div>

        {isSubmitted ? (
          <div className="submitted-banner-modern">
            <div className="success-icon-wrapper">
              <FontAwesomeIcon icon={faCheck} className="success-icon" />
            </div>
            <h3>Feedback Submitted</h3>
            <p>This feedback has been submitted and cannot be edited.</p>
            {selectedCycle.pdf_file && (
              <button className="glass-button primary" onClick={() => openOrDownloadPdf(selectedCycle.id, 'download')}>
                <FontAwesomeIcon icon={faFilePdf} /> Download PDF
              </button>
            )}
          </div>
        ) : (
          <div className="form-progress-modern">
            <div className="progress-bar-modern">
              <div 
                className="progress-fill-modern" 
                style={{ width: `${((activeSection + 1) / allSections.length) * 100}%` }}
              />
            </div>
            <span className="progress-text-modern">Section {activeSection + 1} of {allSections.length}</span>
          </div>
        )}

        <div className="form-sections-nav-modern">
          {allSections.map((section, idx) => (
            <button
              key={section.id}
              className={`section-nav-btn-modern ${idx === activeSection ? 'active' : ''} ${idx < activeSection ? 'completed' : ''}`}
              onClick={() => setActiveSection(idx)}
              disabled={isSubmitted}
            >
              <span className="section-number">{idx + 1}</span>
              <span className="section-title">{section.section_title}</span>
            </button>
          ))}
        </div>

        <div className="form-section-content-modern">
          {currentSection && (
            <div className="section-card">
              <div className="section-header-modern">
                <h2>{currentSection.section_title}</h2>
                {currentSection.section_description && (
                  <p className="section-description-modern">{currentSection.section_description}</p>
                )}
              </div>

              <div className="questions-container-compact">
                {currentSection.questions?.map((question, idx) => (
                  <div key={question.id} className={`question-item-compact ${question.required ? 'required' : ''}`}>
                    <div className="question-row-compact">
                      <span className="question-number-compact">{idx + 1}.</span>
                      <p className="question-text-compact">
                        {question.question_text}
                        {question.required && <span className="required-star">*</span>}
                      </p>
                    </div>
                    <div className="question-input-compact">
                      {renderQuestionInput(question)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Signature Section */}
        {isSubmitted && (
          <div className="signature-section-modern">
            <h2><FontAwesomeIcon icon={faPenNib} /> Digital Signature</h2>
            
            {signatureData ? (
              <div className="saved-signature-modern">
                <img src={signatureData} alt="Digital signature" className="signature-preview-modern" />
                <div className="signature-details-modern">
                  <p className="rep-name"><strong>{representativeName}</strong></p>
                  {designation && <p className="rep-designation">{designation}</p>}
                  <span className="signed-badge"><FontAwesomeIcon icon={faCheck} /> Signed</span>
                </div>
              </div>
            ) : (
              <div className="signature-required-modern">
                <p className="signature-info-text">Please provide your digital signature to complete the feedback form.</p>
                
                <div className="signature-form-fields-modern">
                  <div className="form-group-modern">
                    <label>Representative Name *</label>
                    <input
                      type="text"
                      className="glass-input"
                      value={representativeName}
                      onChange={e => setRepresentativeName(e.target.value)}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="form-group-modern">
                    <label>Designation</label>
                    <input
                      type="text"
                      className="glass-input"
                      value={designation}
                      onChange={e => setDesignation(e.target.value)}
                      placeholder="e.g., CEO, Project Manager"
                    />
                  </div>
                </div>

                <button 
                  className="glass-button primary sign-btn"
                  onClick={() => setShowSignaturePad(true)}
                  disabled={!representativeName.trim()}
                >
                  <FontAwesomeIcon icon={faPenNib} /> Sign Now
                </button>
              </div>
            )}
          </div>
        )}

        <div className="form-actions-modern">
          {!isSubmitted && (
            <>
              <button 
                className="glass-button"
                onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
                disabled={activeSection === 0}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Previous
              </button>
              
              {activeSection < allSections.length - 1 ? (
                <button 
                  className="glass-button primary"
                  onClick={() => setActiveSection(Math.min(allSections.length - 1, activeSection + 1))}
                >
                  Next <FontAwesomeIcon icon={faArrowRight} />
                </button>
              ) : (
                <button 
                  className="glass-button success"
                  onClick={submitFeedback}
                  disabled={submitting}
                >
                  <FontAwesomeIcon icon={faSave} /> {submitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              )}
            </>
          )}
          
          {isSubmitted && !signatureData && (
            <button 
              className="glass-button primary"
              onClick={() => setShowSignaturePad(true)}
            >
              <FontAwesomeIcon icon={faPenNib} /> Add Signature
            </button>
          )}
          
          {isSubmitted && signatureData && (
            <button className="glass-button primary" onClick={generatePdf}>
              <FontAwesomeIcon icon={faFilePdf} /> Download PDF
            </button>
          )}
        </div>

        {/* Signature Pad Modal */}
        {showSignaturePad && (
          <div className="modal-overlay" onClick={() => setShowSignaturePad(false)}>
            <div className="modal signature-modal-modern" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3><FontAwesomeIcon icon={faPenNib} /> Digital Signature</h3>
                <button className="close-btn" onClick={() => setShowSignaturePad(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <p className="modal-subtitle">Please sign within the box below using your mouse or touch</p>
              
              <div className="signature-canvas-container-modern">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="signature-canvas"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              
              <div className="signature-actions-modern">
                <button className="glass-button" onClick={clearSignature}>
                  <FontAwesomeIcon icon={faTimes} /> Clear
                </button>
                <button className="glass-button primary" onClick={saveSignature}>
                  <FontAwesomeIcon icon={faSave} /> Save Signature
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="loading-overlay">
            <div className="spinner" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="client-feedback-page">
      <div className="page-header">
        <h1>My Feedback Forms</h1>
        <p>Complete and submit your service feedback forms</p>
      </div>

      <div className="cycles-list-modern">
        {cycles.length === 0 ? (
          <div className="empty-state-modern">
            <div className="empty-icon-wrapper">
              <FontAwesomeIcon icon={faClipboardCheck} className="empty-icon" />
            </div>
            <h3>No Feedback Forms</h3>
            <p>You don't have any pending or submitted feedback forms at the moment.</p>
          </div>
        ) : (
          cycles.map(cycle => (
            <div 
              key={cycle.id} 
              className={`cycle-card-modern status-${cycle.status.toLowerCase()}`}
              onClick={() => loadCycle(cycle.id)}
            >
              <div className="cycle-header-modern">
                <div className="cycle-title-section">
                  <h3>{cycle.project_title}</h3>
                  <span className="template-name">{cycle.template_name}</span>
                </div>
                <span className={`status-badge-modern ${cycle.status.toLowerCase()}`}>
                  {cycle.status === 'PENDING' && <><FontAwesomeIcon icon={faClock} /> Pending</>}
                  {cycle.status === 'SUBMITTED' && <><FontAwesomeIcon icon={faCheck} /> Submitted</>}
                </span>
              </div>
              <div className="cycle-details-modern">
                <div className="detail-row">
                  <span className="detail-label">Period:</span>
                  <span className="detail-value">
                    {new Date(cycle.cycle_start_date).toLocaleDateString()} - {new Date(cycle.cycle_end_date).toLocaleDateString()}
                  </span>
                </div>
                {cycle.due_date && (
                  <div className="detail-row">
                    <span className="detail-label">Due:</span>
                    <span className={`detail-value ${new Date(cycle.due_date) < new Date() && cycle.status === 'PENDING' ? 'overdue' : ''}`}>
                      {new Date(cycle.due_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className="progress-indicator-modern">
                  <div className="progress-bar-container">
                    <div 
                      className="progress-fill-mini" 
                      style={{ width: `${((cycle.answered_questions || 0) / (cycle.total_questions || 1)) * 100}%` }}
                    />
                  </div>
                  <span className="progress-text">{cycle.answered_questions || 0} of {cycle.total_questions || 0} answered</span>
                </div>
              </div>
              <div className="cycle-action-modern">
                <button className={`glass-button ${cycle.status === 'PENDING' ? 'primary' : ''}`}>
                  {cycle.status === 'PENDING' ? <><FontAwesomeIcon icon={faArrowRight} /> Complete Now</> : 'View Details'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner" />
        </div>
      )}
    </div>
  );
};

export default ClientFeedback;
